import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';
import { SequenceJsonModel } from '../../sequence.json.model';
import { SequenceService } from '../../sequence.service';
import { SequenceActions } from './sequence.actions';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { SequenceEditComponent } from '../../sequence-edit/sequence-edit.component';
import { CopyDialogComponent } from '../../../core/components/copy-dialog/copy-dialog.component';

export interface SequenceStateModel {
  sequences: SequenceJsonModel[]
}

@State<SequenceStateModel>({
  name: 'sequence',
  defaults: {
    sequences: []
  }
})
@Injectable()
export class SequenceState {
  private readonly sequenceService = inject(SequenceService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: SequenceStateModel) {
    return state;
  }

  @Selector()
  static getSequences(state: SequenceStateModel) {
    return state.sequences;
  }

  @Selector()
  static getAmountOfSequences(state: SequenceStateModel) {
    return state.sequences?.length ?? 0;
  }

  @Selector()
  static sequencesIsEmpty(state: SequenceStateModel) {
    return (state.sequences?.length ?? 0) <= 0;
  }

  @Action(SequenceActions.Fetch)
  onFetchSequences(ctx: StateContext<SequenceStateModel>, action: SequenceActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.sequenceService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new SequenceActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(SequenceActions.FetchWithFilters)
  onFetchWithFiltersLearningUnits(ctx: StateContext<SequenceJsonModel>, action: SequenceActions.FetchWithFilters) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.sequenceService.getAllWithPagination({
      queryParams: {
        filter: action.filters,
      } }, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => ctx.dispatch(new SequenceActions.FetchSuccess(res[0])))
    );
  }

  @Action(SequenceActions.FetchSuccess)
  onFetchSequencesSuccess(ctx: StateContext<SequenceStateModel>, { payload }: SequenceActions.FetchSuccess) {
    ctx.setState({ sequences: payload });
  }

  @Action(SequenceActions.FetchAppend)
  onFetchAppendSequences(ctx: StateContext<SequenceStateModel>, action: SequenceActions.FetchAppend) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.sequenceService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new SequenceActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(SequenceActions.FetchSuccessAppend)
  onFetchSequencesSuccessAppend(ctx: StateContext<SequenceStateModel>, { payload }: SequenceActions.FetchSuccess) {
    const state = ctx.getState();
    ctx.setState({
      sequences: [...state.sequences, ...payload]
    });
  }

  @Action(SequenceActions.Add)
  onAdd(ctx: StateContext<SequenceStateModel>, { payload, filters, type }: SequenceActions.Add) {
    payload.kind = type;
    return this.dialog.open<SequenceEditComponent, SequenceJsonModel, SequenceJsonModel>(SequenceEditComponent, {
      minWidth: '65vw',
      maxHeight: '90vh',
      data: payload
    })
      .afterClosed()
      .pipe(
        filter((sequence): sequence is SequenceJsonModel => !!sequence),
        switchMap(sequence => this.sequenceService.createAndSave(sequence)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new SequenceActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new SequenceActions.Fetch());
          }
        })
      );
  }

  @Action(SequenceActions.Copy)
  onCopy(ctx: StateContext<SequenceStateModel>, { payload, filters, type }: SequenceActions.Add) {
    payload.kind = type;
    return this.dialog.open<CopyDialogComponent, SequenceJsonModel, SequenceJsonModel>(CopyDialogComponent, {
      minWidth: '65vw',
      maxHeight: '90vh',
      data: payload
    })
      .afterClosed()
      .pipe(
        filter((sequence): sequence is SequenceJsonModel => !!sequence),
        switchMap(sequence => this.sequenceService.createAndSave(sequence)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new SequenceActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new SequenceActions.Fetch());
          }
        })
      );
  }

  @Action(SequenceActions.Edit)
  onEdit(ctx: StateContext<SequenceStateModel>, { payload, filters }: SequenceActions.Edit) {
    return this.dialog.open<SequenceEditComponent, SequenceJsonModel, SequenceJsonModel>(SequenceEditComponent, {
      minWidth: '65vw',
      maxHeight: '90vh',
      data: payload
    })
      .afterClosed()
      .pipe(
        filter((sequence): sequence is SequenceJsonModel => !!sequence),
        switchMap(sequence => this.sequenceService.update(sequence)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new SequenceActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new SequenceActions.Fetch());
          }
        })
      );
  }

  @Action(SequenceActions.Delete)
  onDelete(ctx: StateContext<SequenceJsonModel>, { payload }: SequenceActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.name,
        itemType: 'Lernsequenz',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new SequenceActions.Fetch());
        })
      );
  }
}
